import React, { useEffect, useMemo, useRef } from "react";
import { Form } from "antd"
import SvgIcons from "../layout/assets/SvgIcons"
// import { useDrop } from "react-dnd"
import HoverWrapper from "./components/HoverWrapper";
import moment from "moment";
import dayjs from "dayjs"
import { useDroppable } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { createFormValuesSlicer } from "../../utils/formsHelper";
import { isDateObject, safeDayjs } from "../../utils/safeDate.js";
import { useTranslation } from 'react-i18next';

const availableWidth = { "12": "50%", "24": "100%" }

// Drop zone component for inserting elements between form elements
// The hook lives in the inner component so an idle canvas registers no insertion
// zones at all. Calling useDroppable above the "not dragging" early-return kept two
// droppables per field registered with dnd-kit permanently, which it then measured
// on every drag.
const FormDropZone = (props) => {
    if (!props.isDraggingWidget && !props.isDraggingFormElement && !props.isDraggingSectionElement) return null;
    return <FormDropZoneInner {...props} />;
};

const FormDropZoneInner = ({ position, elementId }) => {
    const { t } = useTranslation();
    const { setNodeRef, isOver } = useDroppable({
        id: `insert-${position}-${elementId}`,
        data: {
            type: 'insertion-zone',
            position: position, // 'before' or 'after'
            targetElementId: elementId,
            accepts: ['widget', 'form-element', 'section-element']
        }
    });

    return (
        <div 
            ref={setNodeRef}
            style={{
                position: "absolute",
                height: "20px",
                width: "100%",
                top: position === "before" ? "-10px" : "auto",
                bottom: position === "after" ? "-10px" : "auto",
                left: "0",
                zIndex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                transition: "all 0.2s ease",
                backgroundColor: isOver ? "rgba(24, 144, 255, 0.08)" : "transparent",
                borderRadius: "6px",
                cursor: "pointer",
                pointerEvents: "auto",
            }}
        >
            {/* Always show a subtle line when dragging */}
            <div
                style={{
                    width: isOver ? "90%" : "50%",
                    height: "2px",
                    backgroundColor: isOver ? "#1890ff" : "#e6f4ff",
                    transition: "all 0.3s ease",
                    opacity: isOver ? 1 : 0.5,
                    borderRadius: "1px",
                    boxShadow: isOver ? "0 0 6px rgba(24, 144, 255, 0.3)" : "none",
                }}
            />
            
            {/* The + icon that appears on hover */}
            {isOver && (
                <div
                    className="insertion-plus-icon"
                    style={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        width: "24px",
                        height: "24px",
                        backgroundColor: "#1890ff",
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "white",
                        fontSize: "16px",
                        fontWeight: "bold",
                        boxShadow: "0 3px 10px rgba(24, 144, 255, 0.4)",
                        zIndex: 1,
                        border: "2px solid white",
                    }}
                >
                    +
                </div>
            )}
            
            {/* Helper text on hover */}
            {isOver && (
                <div
                    style={{
                        position: "absolute",
                        top: "50%",
                        left: "calc(50% + 40px)",
                        transform: "translateY(-50%)",
                        backgroundColor: "#1890ff",
                        color: "white",
                        padding: "3px 6px",
                        borderRadius: "3px",
                        fontSize: "11px",
                        fontWeight: "500",
                        boxShadow: "0 2px 6px rgba(24, 144, 255, 0.3)",
                        zIndex: 2,
                        whiteSpace: "nowrap",
                    }}
                >
                    {t('Insert')} {position}
                </div>
            )}
        </div>
    );
};

// Handing a field a slice of the values it can actually read - rather than the whole
// store, whose identity changes on every keystroke - is what lets React.memo skip it.
// On a 400-field form that is the difference between ~200ms and ~45ms per character.
// READS_ALL_FORM_VALUES / fieldValueDeps live in formsHelper so the request view
// (RequestForm.jsx) derives the same dependencies.

// Sortable item component for form elements
const SortableFormItem = React.memo(({ data, index, activeDragItem, isDraggingWidget, isDraggingFormElement, isDraggingSectionElement, ...rest }) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ 
        id: data.id,
        data: {
            type: 'form-element',
            elementId: data.id,
            formType: data.formType,
            originalData: data
        },
        // Add activation constraint to prevent accidental drags
        activationConstraint: {
            distance: 8, // Require 8px of movement before starting drag
        }
    });
    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        width: data.span ? availableWidth[data.span] : "100%",
        marginBottom: "2px",
        opacity: isDragging ? 0.5 : 1,
        zIndex: isDragging ? 50 : 'auto', // Further reduced z-index to not interfere with dropdowns
        cursor: isDragging ? 'grabbing' : 'default', // Only show grab cursor when actually dragging
        position: 'relative', // Add position relative for the drop zones
    };

    // Handle keyboard events to prevent interference with typing
    const handleKeyDown = (event) => {
        // Prevent space key and other keys from triggering drag operations
        if (event.target.tagName === 'INPUT' || event.target.tagName === 'TEXTAREA' || event.target.contentEditable === 'true') {
            event.stopPropagation();
        }
    };

    const isFirstElement = index === 0;

    return (
        <div 
            ref={setNodeRef} 
            style={style} 
            {...attributes} 
            {...listeners}
            onKeyDown={handleKeyDown}
            onMouseDown={(e) => {
                // Allow button clicks and dropdown interactions to work - don't start drag if clicking on buttons or select elements
                const target = e.target;
                const isButton = target.closest('button') || target.closest('.ant-btn') || target.closest('.widget-actions');
                const isSelect = target.closest('.ant-select') || target.closest('.ant-select-selector') || target.closest('.ant-select-dropdown');
                const isInput = target.closest('input') || target.closest('textarea') || target.closest('.ant-input');
                const isUserRoleDropdown = target.closest('.custom-dropdown-container') || target.closest('.top-dropdown-label') || target.closest('.custom-dropdown');
                if (isButton || isSelect || isInput || isUserRoleDropdown) {
                    e.stopPropagation();
                    return;
                }
            }}
        >
            {/* Drop zone before the first element */}
            {isFirstElement && (
                <FormDropZone 
                    position="before" 
                    elementId={data.id} 
                    isDraggingWidget={isDraggingWidget}
                    isDraggingFormElement={isDraggingFormElement}
                    isDraggingSectionElement={isDraggingSectionElement}
                />
            )}
            
            <HoverWrapper 
                spaceData={rest.spaceData || {}} 
                data={data} 
                isDragging={isDragging}
                activeDragItem={data.formType === "section" ? activeDragItem : undefined}
                {...rest}
            />
            
            {/* Drop zone after each element */}
            <FormDropZone 
                position="after" 
                elementId={data.id} 
                isDraggingWidget={isDraggingWidget}
                isDraggingFormElement={isDraggingFormElement}
                isDraggingSectionElement={isDraggingSectionElement}
            />
        </div>
    );
});
SortableFormItem.displayName = "SortableFormItem";

const FormBody = (props) => {
    const { t } = useTranslation();
    // `defaultValues` is pulled out of `rest` on purpose: only the effect below reads
    // it, and leaving it in the spread pushed a new identity into every field.
    const { formType, widgetClickHandler, activeElement, formElements = [], activeDragItem, handleSectionReorder, previewmode = false, passFormValues, defaultValues, ...rest } = props
    const [form] = Form.useForm();
    // `preserve: true` reads the whole form store rather than only the registered
    // fields. Sections now start collapsed on large forms, so a field inside a closed
    // section is unmounted - without this it would drop out of this snapshot, get
    // pushed up as a smaller defaultValues object, and its saved default value would be
    // silently lost on Save. Same reasoning as RequestForm.jsx.
    let formValues = Form.useWatch([], { form, preserve: true });
    const lastAppliedDefaultsRef = useRef("")
    const lastPassedValuesRef = useRef("")

    useEffect(() => {
        if (!defaultValues || previewmode) {
            return
        }

        const defaultValueString = JSON.stringify(defaultValues)
        if (defaultValueString === lastAppliedDefaultsRef.current) {
            return
        }

        const currentValuesString = JSON.stringify(form.getFieldsValue(true) || {})
        if (defaultValueString !== currentValuesString) {
            form.setFieldsValue({ ...defaultValues })
        }

        lastAppliedDefaultsRef.current = defaultValueString
    }, [defaultValues, previewmode, form])
    
    useEffect(() => {
        if (previewmode) {
            form.resetFields();
            // Using form.resetFields() is better than directly modifying formValues
        }
    }, [previewmode, form]);

    useEffect(() => {
        if(!previewmode) {
            if(passFormValues){
                const safeValues = formValues || {}
                const valuesString = JSON.stringify(safeValues)
                if (valuesString !== lastPassedValuesRef.current) {
                    lastPassedValuesRef.current = valuesString
                    passFormValues(safeValues)
                }
            }
        }
    },[previewmode, formValues, passFormValues])

    const valueModifier = (values) => {
        let result = {}
        if (!values) {
            return {}
        }
        Object.keys(values).map(key => {
            var formats = [
                moment.ISO_8601,
                "MM/DD/YYYY  :)  HH*mm*ss"
            ];
            const value = values[key]
            if (!value) {
                return
            }
            // Anything already date-shaped (Date, live dayjs, or a dayjs that lost
            // its prototype on a round trip) has to be rebuilt into a real dayjs -
            // strict moment parsing only recognises the string forms.
            const isDateShaped = isDateObject(value)
            const isDate = isDateShaped || moment(value, formats, true).isValid()
            if (isDate) {
                result = { ...result, [key]: safeDayjs(value) }
            } else {
                result = { ...result, [key]: value }
            }
        })
        return result
    }

    // Set up droppable for the main form area
    const { setNodeRef, isOver, active } = useDroppable({
        id: 'main-form-droppable',
        data: {
            type: 'main-form',
            accepts: ['form-element', 'section-element', 'widget']
        }
    });    // Check if we're dragging a section element for enhanced visual feedback
    const isDraggingSectionElement = active?.data?.current?.type === 'section-element';
    
    // Check if we're dragging a widget for form insertion zones
    const isDraggingWidget = active?.data?.current?.type === 'widget';
    
    // Check if we're dragging a form element from the main form
    const isDraggingFormElement = active?.data?.current?.type === 'form-element';
    
    // Check if we're dragging any draggable item (for showing insertion zones)
    const isDraggingAnyElement = isDraggingSectionElement || isDraggingFormElement;
    
    // Check if the section element is being dragged over the main form area (not within its own section)
    const isSectionElementOverMainForm = isDraggingSectionElement && isOver;
    
    // Check if there are any sections with elements that could be moved
    const hasSectionElements = formElements.some(element => 
        element.formType === 'section' && element.children && element.children.length > 0
    );

    // Get the IDs of all items for sortable context
    const itemIds = useMemo(() => formElements.map(item => item.id), [formElements]);

    // Conditions only run in preview on the canvas, so a field's own config is its
    // whole dependency set here.
    const sliceRef = useRef(null);
    if (!sliceRef.current) sliceRef.current = createFormValuesSlicer();
    const formValuesFor = (data) => sliceRef.current(data, formValues, null);

    return (        <div 
            className="droppable-container"
            ref={setNodeRef}            style={{
                backgroundColor: isOver ? (isSectionElementOverMainForm ? "#e6f4ff" : "#f0f8ff") : "white",
                border: isOver ? (isSectionElementOverMainForm ? "3px dashed #1890ff" : "2px dashed #1890ff") : "1px dashed transparent",
                borderRadius: "8px",
                transition: "all 0.2s ease",
                padding: isOver && isSectionElementOverMainForm ? "15px" : "10px",
                position: "relative",
                width: "100%",
                overflow: "visible", // Ensure dropdowns can escape container boundaries
                boxShadow: isOver && isSectionElementOverMainForm ? "0 4px 12px rgba(24, 144, 255, 0.2)" : "none"
            }}
        >            {
                formElements.length > 0 ?
                    <Form form={form} layout="vertical" style={{ display: "flex", flexWrap: "wrap", width: "100%", gap: "0", overflow: "visible" }}>                        {/* Enhanced visual overlay when dragging section elements - only show when over main form */}
                        {isSectionElementOverMainForm && (
                            <div style={{
                                position: "absolute",
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                backgroundColor: "rgba(24, 144, 255, 0.05)",
                                border: "2px dashed #1890ff",
                                borderRadius: "8px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: "16px",
                                fontWeight: "600",
                                color: "#1890ff",
                                zIndex: 10,
                                pointerEvents: "none"
                            }}>
                                {t('Drop anywhere in the main form')}
                            </div>
                        )}
                        
                        <SortableContext 
                            items={itemIds} 
                            strategy={verticalListSortingStrategy}
                        >
                            {
                                formElements.map((data, index) => {
                                    return (                                        
                                    <SortableFormItem
                                            key={data.id}
                                            data={data}
                                            index={index}
                                            activeDragItem={activeDragItem}
                                            isDraggingWidget={isDraggingWidget}
                                            isDraggingFormElement={isDraggingFormElement}
                                            isDraggingSectionElement={isDraggingSectionElement}
                                            form={form}
                                            formValues={formValuesFor(data)}
                                            widgetClickHandler={widgetClickHandler}
                                            isActive={activeElement?.id === data.id}
                                            activeElement={data.formType === "section" ? activeElement : undefined}
                                            formElements={formElements}                                            handleSectionReorder={handleSectionReorder}
                                            {...rest}
                                        />
                                    )
                                })
                            }
                        </SortableContext>                          {/* Enhanced drop zone especially for elements being dragged out of sections */}                        <div style={{
                            width: "100%",
                            height: isOver ? (isSectionElementOverMainForm ? "60px" : "40px") : "20px",
                            backgroundColor: isOver ? (isSectionElementOverMainForm ? "#1890ff" : "#e6f4ff") : "transparent",
                            border: isOver ? (isSectionElementOverMainForm ? "3px solid #1890ff" : "2px dashed #1890ff") : "1px dashed transparent",
                            borderRadius: "8px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: isOver && isSectionElementOverMainForm ? "14px" : "12px",
                            color: isOver ? (isSectionElementOverMainForm ? "#fff" : "#666") : "#666",
                            fontWeight: isOver && isSectionElementOverMainForm ? "600" : "400",
                            transition: "all 0.2s ease",
                            marginTop: "10px",
                            boxShadow: isOver && isSectionElementOverMainForm ? "0 2px 8px rgba(24, 144, 255, 0.3)" : "none"
                        }}>
                            {isOver ? (
                                isSectionElementOverMainForm ? "✓ Drop here to move to main form" : "Drop here to add to main form"
                            ) : (
                                hasSectionElements && !active ? (
                                    <div style={{ 
                                        fontSize: "11px", 
                                        color: "#999", 
                                        fontStyle: "italic" 
                                    }}>
                                        💡 Tip: Drag elements from sections here to move them to the main form
                                    </div>
                                ) : ""
                            )}
                        </div>
                    </Form>
                    :
                    <div style={{ 
                        minHeight: "200px", 
                        display: "flex", 
                        flexDirection: "column", 
                        alignItems: "center", 
                        justifyContent: "center",
                        border: "2px dashed #ddd",
                        borderRadius: "0.75rem",
                        padding: "30px"
                    }}>
                        <SvgIcons type="emptyForm" />
                        <div className="text-regular text-color-primary">{t('Drag and drop fields to create the form.')}</div>
                    </div>
            }
        </div>
    )
}

export default FormBody
